import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  Comparison,
  CurrentItem,
  ReplacementOffer,
  ToggleState,
  fadeInOut,
} from '@dgx-pr-spa/shared/util';

@Component({
  selector: 'app-compare-container',
  templateUrl: './compare-container.component.html',
  styleUrls: ['./compare-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut],
})
export class CompareContainerComponent implements OnChanges {
  /**
   * Receives current item data to be displayed
   */
  @Input() currentItem = {} as CurrentItem;

  /**
   * Receives an array of offers to be displayed
   */
  @Input() comparisonsSelected = [] as ReplacementOffer[];

  /**
   * Array of currentItem & offers combined
   */
  comparisons = [] as Comparison[];

  /**
   * Tracks medium overlay open/closed state
   */
  isMediumOpen = false;

  /**
   * Tracks large overlay open/closed state
   */
  isLargeOpen = false;

  /**
   * Emits when a comparsion select is clicked in the large overlay
   */
  @Output() comparisonSelected: EventEmitter<Event> = new EventEmitter();

  /**
   * Emits when the compare cta button is clicked
   */
  @Output() compareClicked: EventEmitter<ReplacementOffer[]> =
    new EventEmitter();

  /**
   * Emits when `compare models` cta is clicked to open medium overlay
   */
  @Output() compareModelsClicked: EventEmitter<Event> = new EventEmitter();

  /**
   * Emits largeComparisonRemoved event containing card data back to the parent component when large overlay card remove clicked
   */
  @Output()
  largeComparisonRemoved: EventEmitter<ReplacementOffer> = new EventEmitter();

  /**
   * Emits mediumComparisonRemoved event containing card data back to the parent component when medium overlay card remove clicked
   */
  @Output()
  mediumComparisonRemoved: EventEmitter<ReplacementOffer> = new EventEmitter();

  /**
   * Emits a toggledMediumOverlay event back to the parent component when medium overlay is open/closed
   */
  @Output() toggledMediumOverlay: EventEmitter<string> = new EventEmitter();

  /**
   * Emits a toggledLargeOverlay event back to the parent component when large overlay is open/closed
   */
  @Output() toggledLargeOverlay: EventEmitter<string> = new EventEmitter();

  /**
   * Emits a clickedOutside event back to the parent component when clicked outside medium overlay
   */
  @Output() clickedOutside: EventEmitter<boolean> = new EventEmitter();

  /**
   * Host Listener detects if click outside medium overlay to close overlay
   */
  @HostListener('document:click', ['$event']) documentClick(event) {
    this.onClickOutsideHandler(event);
  }

  /**
   * Changes consolidates currentItem and offers
   */
  ngOnChanges() {
    this.buildComparisons();
    if (this.isMediumOpen && !this.comparisonsSelected.length) {
      this.toggleMediumOverlay();
    }
    if (this.isLargeOpen && !this.comparisonsSelected.length) {
      this.toggleLargeOverlay();
    }
  }

  /**
   * Combine currentItem and offers options into 1 array
   */
  buildComparisons() {
    this.comparisons = [
      this.currentItem as Comparison,
      ...(this.comparisonsSelected as Comparison[]),
    ];
  }

  /**
   * Click outside medium overlay handler
   */
  onClickOutsideHandler(event) {
    if (this.isClickedOutside(event) && this.isMediumOpen) {
      this.clickedOutside.emit(true);
      this.toggleMediumOverlay();
    }
  }

  /**
   * Return boolean if clicked outside medium overlay
   */
  isClickedOutside(event) {
    const clickedOutside = !event?.target?.closest('.sticky-element');
    const closeClicked = !!event?.target?.closest('.close');
    const compareFooterClicked = !!event?.target?.closest('.compare-footer');

    return !!(clickedOutside && !closeClicked && !compareFooterClicked);
  }

  /**
   * Toggle medium overlay
   */
  toggleMediumOverlay() {
    this.isMediumOpen = !this.isMediumOpen;
    this.toggledMediumOverlay.emit(
      this.isMediumOpen ? ToggleState.open : ToggleState.closed
    );
  }

  /**
   * Toggle large overlay
   */
  toggleLargeOverlay() {
    this.isLargeOpen = !this.isLargeOpen;
    this.toggledLargeOverlay.emit(
      this.isLargeOpen ? ToggleState.open : ToggleState.closed
    );
    if (this.isMediumOpen) {
      this.toggleMediumOverlay();
    }
  }

  /**
   * Remove medium card
   */
  onRemoveMediumCard(offer: ReplacementOffer) {
    this.mediumComparisonRemoved.emit(offer);
    this.buildComparisons();
    if (!this.hasComparisons()) {
      this.toggleMediumOverlay();
    }
  }

  /**
   * Remove large card
   */
  onRemoveLargeCard(offer: ReplacementOffer) {
    this.largeComparisonRemoved.emit(offer);
    this.buildComparisons();
    if (!this.hasComparisons()) {
      this.toggleMediumOverlay();
      this.toggleLargeOverlay();
    }
  }

  /**
   * Return boolean if offers exist
   */
  hasComparisons() {
    return !!this.comparisonsSelected.length;
  }

  /**
   * Compare clicked to open large overlay
   */
  onCompareClick() {
    this.compareClicked.emit(this.comparisonsSelected);
  }

  /**
   * Compare models clicked
   */
  onCompareModelsClick(e: Event) {
    this.compareModelsClicked.emit(e);
    this.toggleMediumOverlay();
  }

  /**
   * Comparison select clicked
   */
  onComparisonSelectClick(e: Event) {
    this.comparisonSelected.emit(e);
  }
}
